import { useState } from "react";
import paymentService from "../../../../services/paymentService";
import { getUser } from "../../../../services/localService";
import logger from "../../../../utils/logger";


export const usePaymentApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetError = () => setError(null);

  const fetchPaymentInstitutions = async (currency, country) => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getPaymentInstitutions(currency, country);

      if (response.status === 200) {
        // Handle nested data structure
        if (response.data?.data?.data) {
          return response.data.data.data;
        } else if (response.data?.data) {
          return response.data.data;
        } else if (Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      } else {
        throw new Error(response.message || "Failed to fetch payment institutions");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to fetch payment institutions";
      setError(errorMessage);
      logger.error("Error fetching payment institutions:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getCountries();

      if (response.status === 200) {
        // Handle nested data structure
        if (Array.isArray(response.data?.data)) {
          return response.data.data;
        } else if (Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      } else {
        throw new Error(response.message || "Failed to fetch countries");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to fetch countries";
      setError(errorMessage);
      logger.error("Error fetching countries:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getAccountTypes();

      if (response.status === 200) {
        return response.data.data; // Access the nested data array
      } else {
        throw new Error(response.message || "Failed to fetch account types");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to fetch account types";
      setError(errorMessage);
      logger.error("Error fetching account types:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getBusinessCategories();

      if (response.status === 200) {
        return response.data.data; // Access the nested data array
      } else {
        throw new Error(response.message || "Failed to fetch business categories");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to fetch business categories";
      setError(errorMessage);
      logger.error("Error fetching business categories:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const submitPaymentSetup = async formData => {
    try {
      setLoading(true);
      setError(null);

      // Get organization ID from current user data
      const currentUser = getUser();
      const organizationId = currentUser?.organizationId || currentUser?.organization?.id;

      if (!organizationId) {
        throw new Error("No organization ID found for current user");
      }

      const response = await paymentService.submitPaymentSetup(formData, organizationId);

      if (response.status === 200 || response.status === 201) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to submit payment setup");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to submit payment setup";
      setError(errorMessage);
      logger.error("Error submitting payment setup:", err);
      throw err; // Re-throw the error so it can be caught by the component
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentChannels = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getPaymentChannels();

      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch payment channels");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to fetch payment channels";
      setError(errorMessage);
      logger.error("Error fetching payment channels:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getPaymentClients();

      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch payment clients");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to fetch payment clients";
      setError(errorMessage);
      logger.error("Error fetching payment clients:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getPaymentProviders();

      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch payment providers");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to fetch payment providers";
      setError(errorMessage);
      logger.error("Error fetching payment providers:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationPaymentSetups = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get organization ID from current user data
      const currentUser = getUser();
      const organizationId = currentUser?.organizationId || currentUser?.organization?.id;

      if (!organizationId) {
        throw new Error("No organization ID found for current user");
      }

      const response = await paymentService.getOrganizationPaymentSetups(organizationId);

      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch organization payment setups");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to fetch organization payment setups";
      setError(errorMessage);
      logger.error("Error fetching organization payment setups:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    resetError,
    fetchPaymentInstitutions,
    fetchCountries,
    fetchPaymentChannels,
    fetchPaymentClients,
    fetchPaymentProviders,
    fetchOrganizationPaymentSetups,
    fetchAccountTypes,
    fetchBusinessCategories,
    submitPaymentSetup,
  };
};
